import { createSignal } from 'solid-js';
import type { RgbColor } from 'solid-color';
import { Color } from '@technology/web3d';
import type { Markup } from '@technology/web3d-plugin-markup';
import tinycolor from 'tinycolor2';
import type {
  Measurement,
  MeasurementsCapabilityProvider,
} from '@trimble/web3d-providers';
import { rgbColorToCssHexString } from '@trimble/utilities';
import type { Web3DViewerWrapper } from '@trimble/web3d-utils';
import type { BaseMeasurementController } from './measurement-controller.types';

const DEFAULT_COLOR: RgbColor = { r: 236, g: 0, b: 182 };

export { DEFAULT_COLOR as DEFAULT_MEASUREMENT_COLOR };

export interface MeasurementControllerOptions<T extends Measurement> {
  applyMarkupProperties?(
    markup: Markup,
    viewerWrapper: Web3DViewerWrapper,
    labelValue: string | undefined
  ): void;
  getExtraUpdateFields?(): Partial<T>;
  onInitialize?(measurement: T): void;
  defaultColor?: RgbColor;
}

export function createBaseMeasurementController<T extends Measurement>(
  provider: MeasurementsCapabilityProvider,
  options: MeasurementControllerOptions<T> = {}
): BaseMeasurementController {
  let currentMeasurement: T | undefined;

  const defaultColor = options.defaultColor ?? DEFAULT_COLOR;
  const [color, setColorSignal] = createSignal<RgbColor>(defaultColor);
  const [label, setLabelSignal] = createSignal<string | undefined>(undefined);

  const update = (): void => {
    if (!currentMeasurement) return;

    const colorValue = color();
    const labelValue = label()?.trim();

    for (const viewerWrapper of provider.viewerWrappers()) {
      for (const markup of provider.getViewerMarkups(
        viewerWrapper.viewer,
        currentMeasurement
      )) {
        markup.color = new Color(rgbColorToCssHexString(colorValue));
        options.applyMarkupProperties?.(markup, viewerWrapper, labelValue);
      }
    }

    provider.updateMeasurement({
      ...currentMeasurement,
      color: tinycolor(colorValue).toRgb(),
      label: labelValue,
      ...options.getExtraUpdateFields?.(),
    } as T);
  };

  const setColor = (value: RgbColor): void => {
    setColorSignal(value);
    update();
  };

  const setLabel = (value: string | undefined): void => {
    setLabelSignal(value);
    update();
  };

  const initialize = (measurement: Measurement): void => {
    currentMeasurement = measurement as T;
    setColorSignal(measurement.color ?? defaultColor);
    setLabelSignal(measurement.label);
    options.onInitialize?.(currentMeasurement);
  };

  const cleanup = (): void => {
    currentMeasurement = undefined;
  };

  return {
    color,
    label,
    setColor,
    setLabel,
    initialize,
    update,
    cleanup,
  };
}
